import { useMemo } from 'react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import Break from '@/components/elements/Break';
import BaseButton from '@/components/elements/Button/BaseButton';
import ModalBase, { ModalBasicProps } from '@/components/layouts/Modal/Base';
import { useUserContext } from '@/contexts/user';
import { hasDmmLoginCookieInEvenOnce } from '@/lib/auth';
import { getLoginUrl } from '@/lib/dmm/accounts';
import { defaultTheme } from '@/utility/theme';

import { ButtonWrapper, Container, Notice, Text, Title } from './style';

interface LoginModalProps extends ModalBasicProps {
  backPath?: string;
  urlParams?: URLSearchParams;
}

type ContentType = 'loginHasNoCookie' | 'loginHasCookie' | 'signup' | 'waiting';

const Button: React.FC = ({ children }) => (
  <BaseButton
    size="m"
    bgColor="primary"
    radius="l"
    width={['100%', '320px', '320px', '320px']}
    height="56px"
    fontWeight="bold"
  >
    {children}
  </BaseButton>
);

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  handleClose,
  backPath,
  urlParams,
}) => {
  const { isLoggedIn, isGuest, isTemporaryMember, isWaiting } =
    useUserContext();

  const contentType = useMemo<ContentType>(() => {
    if (!isLoggedIn) {
      if (hasDmmLoginCookieInEvenOnce()) {
        return 'loginHasCookie';
      }
      return 'loginHasNoCookie';
    }
    if (isTemporaryMember && isWaiting) {
      return 'waiting';
    }
    return 'signup';
  }, [isLoggedIn, isGuest, isTemporaryMember, isWaiting]);

  const loginPath = useMemo(
    () =>
      getLoginUrl(`${backPath}${urlParams ? `?${urlParams.toString()}` : ''}`),
    [backPath, urlParams],
  );

  return (
    <ModalBase
      isOpen={isOpen}
      handleClose={() => handleClose()}
      backgroundColor={defaultTheme.background.white}
    >
      <Container>
        <Title data-testid="title">
          ここから先へ進むには
          <Break breakpoint="large" isHidden />
          DMM競輪に登録してください
        </Title>
        {contentType === 'waiting' && (
          <Text data-testid="text">
            DMM競輪への登録が完了しておりません。
            <br />
            本人確認のため、公的書類を提出してください。
          </Text>
        )}
        <ButtonWrapper data-testid="button">
          {contentType === 'loginHasCookie' && (
            <a href={loginPath}>
              <Button>ログイン</Button>
            </a>
          )}
          {(['loginHasNoCookie', 'signup'] as ContentType[]).includes(
            contentType,
          ) && (
            <Link to="/signup">
              <Button>利用登録する（無料）</Button>
            </Link>
          )}
          {contentType === 'waiting' && (
            <a href={import.meta.env.VITE_VERIFICATION_URL}>
              <Button>公的書類を提出する</Button>
            </a>
          )}
        </ButtonWrapper>
        {(['loginHasNoCookie', 'loginHasCookie'] as ContentType[]).includes(
          contentType,
        ) && (
          <Notice data-testid="notice">
            {contentType === 'loginHasNoCookie' && (
              <>
                アカウントをお持ちの場合は<a href={loginPath}>ログイン</a>
                してください。
              </>
            )}
            {contentType === 'loginHasCookie' && (
              <>
                アカウントをお持ちでない場合は
                <Link to="/signup">会員登録（無料）</Link>をしてください。
              </>
            )}
          </Notice>
        )}
      </Container>
    </ModalBase>
  );
};

export default LoginModal;
